import { dateFormat, DateFormatType } from 'utils/helpers/date'

/**
 * Browse admin APIs return timestamps in UTC (some without an explicit `Z`),
 * and the admin displays them in WIB. Shifting the epoch by +7h and then
 * reading the UTC fields keeps the output independent of the machine timezone.
 */
const WIB_OFFSET_MS = 7 * 60 * 60 * 1000

const HAS_TIMEZONE_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/

/** `offsetMs` shifts the parsed instant; 0 keeps the API's wall clock as-is. */
const toShiftedDate = (dateTime: string | undefined, offsetMs: number) => {
  if (!dateTime) {
    return null
  }

  const normalized = HAS_TIMEZONE_PATTERN.test(dateTime) ? dateTime : `${dateTime}Z`
  const parsed = new Date(normalized)

  return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.getTime() + offsetMs)
}

export const utcToWibDate = (dateTime?: string) => toShiftedDate(dateTime, WIB_OFFSET_MS)

export const formatUtcToWib = (dateTime: string | undefined, format: DateFormatType) => {
  const wibDate = utcToWibDate(dateTime)

  return wibDate ? dateFormat({ date: wibDate, format, utc: true }) : ''
}

/** For timestamps the API already sends in WIB — formats them without the +7h shift. */
export const formatWibAsIs = (dateTime: string | undefined, format: DateFormatType) => {
  const date = toShiftedDate(dateTime, 0)

  return date ? dateFormat({ date, format, utc: true }) : ''
}

/** `datetime-local` inputs expect `YYYY-MM-DDTHH:mm` in local (WIB) time. */
export const utcToWibDateTimeLocal = (dateTime?: string) =>
  formatUtcToWib(dateTime, 'YYYY-MM-DDTHH:mm')

/** `datetime-local` (WIB wall clock) → RFC3339 UTC, e.g. `2026-08-04T00:00:00Z`. */
export const wibToUtcRfc3339 = (dateTimeLocal?: string) => {
  if (!dateTimeLocal) {
    return ''
  }

  const withSeconds = dateTimeLocal.length === 16 ? `${dateTimeLocal}:00` : dateTimeLocal
  const parsed = new Date(`${withSeconds}+07:00`)

  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().replace(/\.\d{3}Z$/, 'Z')
}
